import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { walletModalOpenAtom } from '@/atoms/wallet-modal';
import type { WebsiteSupportedChains } from '@/lib/constants';
import {
  transferIdInformationQueryOptions,
  transferIdQueryOptions,
} from '@/lib/query-options';
import { normalizeReceiverAddress } from '@/lib/utils';
import { useEvmUsdtApproval, useTronUsdtApproval } from './use-approval';
import { useTransferMutation } from './use-transfer';

interface TransferParams {
  fromAddress: string | undefined;
  fromAmount: string;
  receiver: string;
  fromChain: WebsiteSupportedChains;
  toChain: WebsiteSupportedChains;
  defaultContractAddress: string;
}

export function useTransferFlow() {
  const { t } = useTranslation();
  const setWalletModalOpen = useSetAtom(walletModalOpenAtom);
  const queryClient = useQueryClient();
  const { approve: approveTronUsdt } = useTronUsdtApproval();
  const { approve: approveEvmUsdt } = useEvmUsdtApproval();
  const { approve: approveD9Usdt } = useD9Approval();
  const { mutateAsync: transfer } = useTransferMutation();

  const mutation = useMutation({
    mutationFn: async (params: TransferParams) => {
      if (typeof params.fromAddress !== 'string') {
        setWalletModalOpen(true);
        throw new Error(t('errors.walletNotConnected'));
      }

      // Normalize the receiver address, throw an error if it's not valid
      const normalizedReceiver = normalizeReceiverAddress(
        params.toChain,
        params.receiver,
      );

      const normalizedFromAddress = normalizeReceiverAddress(
        params.fromChain,
        params.fromAddress,
      );

      const userId =
        params.fromChain === 'D9' ? normalizedFromAddress : normalizedReceiver;

      // Get the transferId
      const transferId = await queryClient.fetchQuery(
        transferIdQueryOptions(userId),
      );

      // Check if the transferId is in progress
      const isTransferIdAvailable = await queryClient.fetchQuery(
        transferIdInformationQueryOptions(transferId),
      );

      if (!isTransferIdAvailable) {
        throw new Error(t('errors.transferInProgress'));
      }

      // Approve the contract
      switch (params.fromChain) {
        case 'BSC':
          await approveEvmUsdt.mutateAsync({
            amount: params.fromAmount,
            contractAddress: params.defaultContractAddress,
          });
          break;
        case 'TRON':
          await approveTronUsdt.mutateAsync({
            amount: params.fromAmount,
            contractAddress: params.defaultContractAddress,
          });
          break;
        case 'D9':
          await approveD9Usdt.mutateAsync({
            amount: params.fromAmount,
            // contractAddress: params.defaultContractAddress,
            contractAddress: 'vNNoHwVH8af77P4s1ch14yTy7UTd8w9g2VfYA5fBExZzA7i',
          });
          break;
        default:
          throw new Error(t('errors.unsupportedChain'));
      }

      // Submit transfer
      const result = await transfer({
        fromAddress: normalizedFromAddress,
        toAddress: normalizedReceiver,
        amount: params.fromAmount,
        fromChain: params.fromChain,
        toChain: params.toChain,
        transferId: transferId,
        userId: userId,
      });

      // Return the jobId, let the caller handle the polling
      return result;
    },
    onError(error) {
      console.error('Transfer error', error);
      if ('shortMessage' in error && typeof (error as any).shortMessage === 'string') {
        return toast.error((error as any).shortMessage);
      }
      // Map error messages to translations
      if (error instanceof Error) {
        const errorMessage = error.message;
        // Check if it's the transfer in progress error with details
        if (
          errorMessage.includes('cross-chain transfer') &&
          errorMessage.includes('Request ID')
        ) {
          // Extract details from error message
          const fromMatch = errorMessage.match(/from (\w+)/);
          const toMatch = errorMessage.match(/to (\w+)/);
          const requestIdMatch = errorMessage.match(/Request ID: (\w+)/);
          if (fromMatch && toMatch && requestIdMatch) {
            return toast.error(
              t('errors.transferInProgressWithDetails', {
                fromChain: fromMatch[1],
                toChain: toMatch[1],
                requestId: requestIdMatch[1],
              }),
            );
          }
        }
        if (errorMessage.includes('not enabled') || errorMessage.includes('is disabled')) {
          return toast.error(t('errors.networkCongestion'));
        }
        // Check for other known error patterns
        if (
          errorMessage.includes('transfer') &&
          errorMessage.includes('in progress')
        ) {
          return toast.error(t('errors.transferInProgress'));
        }
      }
      toast.error((error as any).message);
    },
  });

  return mutation;
}


